import { useState, useMemo, useEffect } from 'react';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useContactFilters, type ContactCompletenessChip } from './useContactFilters';
import { useContactActions } from './useContactActions';
import { useContactBulkActions } from './useContactBulkActions';
import { useContactExport } from './useContactExport';
import { useBulkSelection } from '../../../hooks/useBulkSelection';

export const useContactsPage = (canExport = false) => {
    const {
        contacts,
        updateContact,
        refreshContacts,
        createContact,
        deleteContact,
        isLoading,
    } = useCrmData();

    const [completenessChip, setCompletenessChip] =
        useState<ContactCompletenessChip>('all');

    const {
        filters,
        filteredContacts,
        updateSearch,
        chipCounts,
        totalCount,
    } = useContactFilters(contacts, completenessChip);
    const [localCurrentPage, setLocalCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const paginatedContacts = useMemo(() => {
        const start = (localCurrentPage - 1) * pageSize;
        return filteredContacts.slice(start, start + pageSize);
    }, [localCurrentPage, filteredContacts, pageSize]);

    useEffect(() => {
        setLocalCurrentPage(1);
    }, [filters.search, completenessChip]);

    const {
        selectedIds,
        isAllSelected,
        isSomeSelected,
        toggleSelection,
        toggleAll,
        clearSelection,
        getSelectedItems,
    } = useBulkSelection(filteredContacts, paginatedContacts);

    const {
        isFormModalOpen,
        isDeleteModalOpen,
        contactToEdit,
        contactToDelete,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleCloseModals,
        handleSaveContact,
        handleConfirmDelete,
        handleDuplicateContact,
    } = useContactActions({
        createContact: async (data: any) => createContact(data),
        updateContact: async (id: string, data: any) => updateContact(id, data),
        deleteContact: async (id: string) => deleteContact(id),
    });

    const {
        isExporting,
        handleExportAll,
        handleExportSelected,
        handleExportOne,
    } = useContactExport({ filteredContacts, getSelectedItems });

    const {
        bulkActions,
        isBulkDeleteModalOpen,
        setBulkDeleteModalOpen,
        handleBulkDelete,
    } = useContactBulkActions({
        getSelectedItems,
        clearSelection,
        deleteContact,
        onExportSelected: handleExportSelected,
        canExport,
    });

    const [isImportModalOpen, setImportModalOpen] = useState(false);

    return {
        filteredContacts,
        paginatedContacts,
        loading: isLoading,
        isImportModalOpen,
        setImportModalOpen,
        handleOpenImportModal: () => setImportModalOpen(true),
        importContactService: async (data: any) => createContact(data),
        handleImportFinished: async (count: number) => {
            if (count > 0) await refreshContacts();
        },
        filters,
        updateSearch,
        completenessChip,
        setCompletenessChip,
        chipCounts,
        totalCount,
        localCurrentPage,
        setLocalCurrentPage,
        pageSize,
        setPageSize,
        selectedIds,
        isAllSelected,
        isSomeSelected,
        toggleSelection,
        toggleAll,
        clearSelection,
        isFormModalOpen,
        isDeleteModalOpen,
        contactToEdit,
        contactToDelete,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleCloseModals,
        handleSaveContact,
        handleConfirmDelete,
        handleDuplicateContact,
        bulkActions,
        isBulkDeleteModalOpen,
        setBulkDeleteModalOpen,
        handleBulkDelete,
        isExporting,
        handleExportAll,
        handleExportOne,
    };
};
